import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  alpha,
  Stack,
  CircularProgress,
  Checkbox
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { setEmail, setPortfolio, fetchPublicScore } from '../../slices/publicSlice';

export default function HeroSection({ id }) {
  const [portfolio, setPortfolioInput] = useState('');
  const [email, setEmailInput] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fetchStatus = useSelector((state) => state.public.fetchStatus);
  const error = useSelector((state) => state.public.error);

  const parsePortfolioString = (portfolioStr) => {
    // Split by semicolon and filter out empty strings
    const pairs = portfolioStr.split(';').filter(pair => pair.trim());
    
    // Reduce into an object
    return pairs.reduce((acc, pair) => {
      const [ticker, shares] = pair.split(',').map(item => item.trim());
      if (ticker && shares && !isNaN(shares)) {
        acc[ticker] = parseInt(shares, 10);
      }
      return acc;
    }, {});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (portfolio.trim() && email.trim()) {
      const trimmedEmail = email.trim();
      const parsedPortfolio = parsePortfolioString(portfolio.trim());

      // Only proceed if we have a valid portfolio object with at least one entry
      if (Object.keys(parsedPortfolio).length > 0) {
        dispatch(setEmail(trimmedEmail));
        dispatch(setPortfolio(parsedPortfolio));
        dispatch(fetchPublicScore({ 
          email: trimmedEmail, 
          portfolio: parsedPortfolio
        }))
          .unwrap()
          .then(() => {
            navigate('/score/public');
          })
          .catch((err) => {
            console.error('Failed to fetch public score:', err);
          });
      }
    }
  };

  const isFormValid = portfolio.trim() && email.trim() && email.includes('@') && tosAccepted;

  return (
    <Box
      id={id}
      sx={{
        position: 'relative',
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.paper',
        pt: 8,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h1"
            gutterBottom
            sx={{
              mb: { xs: 2, md: 3 },
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Get <i><u>Scientific</u></i> guidance <br/> for your Portfolio
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              mb: { xs: 4, md: 5 },
              maxWidth: '600px',
              mx: 'auto',
              color: 'text.secondary',
            }}
          >
            Get a global score and insights instantly
          </Typography>
        </Box>

        <Paper
          elevation={2}
          sx={{
            p: { xs: 3, md: 4 },
            maxWidth: 'md',
            mx: 'auto',
            backgroundColor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                type="email"
                variant="outlined"
                label="Email Address"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmailInput(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  },
                }}
              />
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  textAlign: 'left',
                  mt: -1,
                  mb: -1
                }}
              >
                Enter your portfolio by concatenating the ticker and quantity of shares like this: TICKER,SHARES; TICKER,SHARES;
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                label="Portfolio"
                placeholder="AAPL,100; MSFT,500; JNJ,150"
                value={portfolio}
                onChange={(e) => setPortfolioInput(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  },
                }}
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  id="tos-checkbox"
                />
                <Typography variant="body2" color="text.secondary">
                  I agree to the <RouterLink to="/terms" target="_blank">Terms of Service</RouterLink>
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={!isFormValid || fetchStatus === 'loading'}
                startIcon={fetchStatus === 'loading' ? <CircularProgress size={20} /> : <TrendingUpIcon />}
                sx={{
                  py: 2,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  }
                }}
              >
                {fetchStatus === 'loading' ? 'Getting Score...' : 'Get My Score'}
              </Button>
            </Stack>
          </form>
        </Paper>

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </Container>
    </Box>
  );
}